import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import SectionHeading from '../components/ui/SectionHeading';

const FeedbackAnalytics = () => {
  // Enhanced Radar Chart for Strategic Maturity
  const maturityRadarOption = {
    radar: {
      indicator: [
        { name: 'Brand Positioning', max: 100 },
        { name: 'AI Orchestration', max: 100 },
        { name: 'MarTech Ops', max: 100 },
        { name: 'Digital UX', max: 100 },
        { name: 'Data Clarity', max: 100 }
      ],
      splitArea: {
        show: false
      },
      axisLine: {
        lineStyle: {
          color: 'rgba(64, 224, 208, 0.2)'
        }
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(64, 224, 208, 0.1)'
        }
      }
    },
    series: [{
      type: 'radar',
      areaStyle: {
        color: 'rgba(64, 224, 208, 0.3)'
      },
      lineStyle: {
        color: '#40E0D0',
        width: 3
      },
      itemStyle: {
        color: '#40E0D0'
      },
      data: [
        {
          value: [85, 45, 60, 90, 70],
          name: 'Global Benchmark'
        }
      ]
    }]
  };

  const pollResultsOption = {
    tooltip: { trigger: 'item' },
    legend: { 
      bottom: '0', 
      left: 'center',
      textStyle: { color: '#94a3b8', fontSize: 10 },
      itemGap: 15
    },
    series: [
      {
        name: 'Strategic Hurdles',
        type: 'pie',
        radius: ['50%', '75%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 8,
          borderColor: 'transparent',
          borderWidth: 2
        },
        label: { show: false },
        data: [
          { value: 42, name: 'Operations Redundancy', itemStyle: { color: '#40E0D0' } },
          { value: 35, name: 'AI Integration Friction', itemStyle: { color: '#FFD700' } },
          { value: 23, name: 'Brand-Performance Gap', itemStyle: { color: '#FF2A2A' } }
        ]
      }
    ]
  };

  const trendOption = {
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { 
      type: 'category', 
      data: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      axisLine: { lineStyle: { color: '#334155' } },
      axisTick: { show: false }
    },
    yAxis: { 
      type: 'value', 
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)' } },
      axisLine: { show: false }
    },
    series: [{
      data: [150, 230, 224, 318],
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      lineStyle: { color: '#40E0D0', width: 4 },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [{ offset: 0, color: 'rgba(64, 224, 208, 0.4)' }, { offset: 1, color: 'rgba(64, 224, 208, 0)' }]
        }
      },
      itemStyle: { color: '#40E0D0', borderWidth: 2, borderColor: '#fff' }
    }]
  };

  return (
    <div className="pt-24 min-h-screen bg-slate-50 dark:bg-brand-dark transition-colors duration-300 pb-24 relative overflow-hidden">
      <div 
        className="bg-brand-darker py-16 relative overflow-hidden border-b border-brand-cyan/20"
      >
        <div 
          className="absolute inset-0 opacity-[0.05]" 
          style={{ 
            backgroundImage: 'linear-gradient(to right,#40E0D0 1px,transparent 1px), linear-gradient(to bottom,#40E0D0 1px,transparent 1px)', 
            backgroundSize: '40px 40px',
            maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
            WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-brand-cyan text-xs uppercase tracking-[0.2em] font-bold block mb-2">Sector Intelligence</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white">Strategic Pulse Analytics</h1>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {[
            { label: 'Total Respondents', value: '1,284', icon: 'Users', color: 'text-brand-cyan' },
            { label: 'Operational Maturity', value: 'B+', icon: 'Shield', color: 'text-brand-yellow' },
            { label: 'AI Readiness Score', value: '64%', icon: 'Cpu', color: 'text-brand-red' }
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                        className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 group hover:border-brand-cyan transition-all" >
              <div className="flex items-center gap-4 mb-4">
                <div className={`p-3 rounded-xl bg-slate-50 dark:bg-brand-darker ${stat.color} group-hover:bg-brand-cyan/10 transition-colors`}>
                  <SafeIcon name={stat.icon} className="w-6 h-6" />
                </div>
                <span className="text-[10px] uppercase font-bold tracking-[0.2em] text-slate-500">{stat.label}</span>
              </div>
              <p className="text-5xl font-heading font-black dark:text-white text-brand-dark">{stat.value}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Strategic Maturity Radar - New Enhanced Visualization */}
          <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 lg:col-span-2">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="w-full md:w-1/2">
                <h3 className="text-2xl font-heading font-bold dark:text-white text-slate-900 mb-6 flex items-center gap-3">
                  <SafeIcon name="Target" className="text-brand-cyan" /> Industry Maturity Benchmark
                </h3>
                <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed mb-8">
                  Our analysis shows that while Digital UX and Brand Positioning are high across most organizations, AI Orchestration remains the single largest growth opportunity for Q4.
                </p>
                <div className="space-y-4">
                  {[
                    { label: "AI Integration Friction", val: "High Impact", color: "text-brand-red" },
                    { label: "Stack Consolidation Opportunity", val: "Moderate", color: "text-brand-yellow" },
                    { label: "Data Integrity Rating", val: "Optimized", color: "text-brand-cyan" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-brand-darker rounded-xl border border-slate-100 dark:border-slate-800">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{item.label}</span>
                      <span className={`text-xs font-black uppercase tracking-widest ${item.color}`}>{item.val}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full md:w-1/2 h-[400px]">
                <ReactECharts option={maturityRadarOption} style={{ height: '100%' }} />
              </div>
            </div>
          </div>

          {/* Primary Poll Findings */}
          <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800">
            <h3 className="text-xl font-heading font-bold dark:text-white text-slate-900 mb-8 flex items-center gap-3">
              <SafeIcon name="PieChart" className="text-brand-cyan" /> Core Strategic Hurdles
            </h3>
            <div className="h-[320px]">
              <ReactECharts option={pollResultsOption} style={{ height: '100%' }} />
            </div>
          </div>

          {/* Engagement Trends */}
          <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800">
            <h3 className="text-xl font-heading font-bold dark:text-white text-slate-900 mb-8 flex items-center gap-3">
              <SafeIcon name="TrendingUp" className="text-brand-yellow" /> Engagement Velocity
            </h3>
            <div className="h-[320px]">
              <ReactECharts option={trendOption} style={{ height: '100%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackAnalytics;